import type { Result, AppError } from '../../error/types';
import { isEnvironment } from './isEnvironment';
import { ENVIRONMENTS } from '../constants/ENVIRONMENTS';

/**
 * @group Functions
 * @summary <blockquote>Utility function to check if the current environment is production using the Result pattern.</blockquote>
 * 
 * @returns Result containing true if the current environment is 'production', false otherwise, or AppError if environment detection fails
 * 
 * @example
 * ```typescript
 * import { isProduction } from '@shared/utils';
 * 
 * const result = isProduction();
 * 
 * result.match({
 *   ok: (isProd) => {
 *     if (isProd) {
 *       console.log('Running in production mode');
 *     }
 *   },
 *   err: (error) => {
 *     console.error(`Environment check failed: ${error.errorMessage}`);
 *   }
 * });
 * ```
 * 
 * @example
 * ```typescript
 * // With environment variables:
 * // VERCEL_ENV=production → returns Ok(true)
 * // NODE_ENV=production    → returns Ok(true)
 * // VERCEL_ENV=preview     → returns Ok(false)
 * // NODE_ENV=development   → returns Ok(false)
 * ```
 */
export function isProduction(): Result<boolean, AppError> {
  return isEnvironment(ENVIRONMENTS.PRODUCTION);
}